import { Dispatch, SetStateAction, useCallback } from "react";
import { useAsyncResult } from "../../Hooks/UseAsyncResult";
import {
  Ev2ExperimentStatusResults,
  ExtensionPipelineIds,
  LinkTypes,
  PipelineStatusMap,
} from "../../Types";

import { getExpEnvironment, getRolloutId } from "../../Shared/shared.utils";
import {
  fetchExtractLinks,
  fetchExperimentPipelineMap,
  fetchPipelines,
} from "../../Api/ado.api";
import { useEv2ExperimentParams } from "../UseCurrentExtension";
import { fetchExperimentStatuses } from "../../Api/ev2.api";
import {
  MPAC_TYPE,
  PROD_TYPE,
  RC_TYPE,
} from "../../Experiment/RolloutDetails/RolloutDetailsTable/RolloutDetailsEv2Panel/ev2TreatmentStages.consts";
const extractLinksCache: Record<number, { ev2PortalLink?: string | null }> = {};

export function useFetchEv2RunStatuses(
  setError: Dispatch<SetStateAction<any>>,
) {
  const { extensionFolder } = useEv2ExperimentParams();

  const func = useCallback(
    async (_disposable, _force, onUpdate) => {
      if (!extensionFolder) {
        return {};
      }
      const rootExtension = extensionFolder.rootExtension;
      if (!rootExtension) {
        return {};
      }
      const repoConfig = await fetchExperimentPipelineMap(rootExtension);

      if (!repoConfig) {
        return {};
      }
      const statusMap = await fetchPipelineRunStatuses(
        repoConfig,
        _disposable.signal,
        onUpdate,
      );
      return statusMap;
    },
    [extensionFolder],
  );

  return useAsyncResult({
    func,
    setError,
  });
}

export async function fetchPipelineRunStatuses(
  repoConfig: ExtensionPipelineIds,
  signal?: AbortSignal,
  onUpdate?: (value: PipelineStatusMap) => void,
) {
  const rolloutIdConfigFilenameMap: Record<
    string,
    { fileName: string; environment: string }
  > = {};

  const pipelineIds = [
    repoConfig?.singleStagePipelineId,
    repoConfig?.unifiedPipelineId,
  ]
    .map((id) => Number(id))
    .filter((id) => !isNaN(id));

  const runs = await Promise.all(
    pipelineIds.map((pipelineId) => fetchPipelines(pipelineId)),
  ).then((runs) =>
    runs.flat().filter((run) => !!run.templateParameters?.ConfigSpecFileName),
  );

  const rolloutIds = await Promise.all(
    runs.map(async (run) => {
      const links =
        extractLinksCache[run.id] ??
        (extractLinksCache[run.id] = await fetchExtractLinks(run.id, [
          LinkTypes.Ev2,
        ]));
      const { ev2PortalLink = "" } = links;
      const rolloutId = getRolloutId(ev2PortalLink);
      if (rolloutId && !rolloutIdConfigFilenameMap[rolloutId]) {
        rolloutIdConfigFilenameMap[rolloutId] = {
          fileName: run.templateParameters?.ConfigSpecFileName,
          environment: getExpEnvironment(
            run.templateParameters?.RolloutAndProgressionType,
          ),
        };
      }
      return rolloutId;
    }),
  ).then((ids) => ids.filter((id): id is string => !!id));

  const ev2StatusesMap = await fetchExperimentStatuses(
    rolloutIds,
    signal,
    onUpdate
      ? (partial: Ev2ExperimentStatusResults) => {
          onUpdate(processEv2Statuses(partial, rolloutIdConfigFilenameMap));
        }
      : undefined,
  );

  return processEv2Statuses(ev2StatusesMap, rolloutIdConfigFilenameMap);
}

/**
 * Helper function to process Ev2ExperimentStatusResults into PipelineStatusMap format to be used by the ExperimentsTable
 * @param ev2StatusesMap - The raw status results fetched from the API, keyed by rolloutId
 * @param rolloutIdConfigFilenameMap - A mapping of rolloutId to its corresponding config file name and environment, used to organize the statuses in the resulting map
 */
const processEv2Statuses = (
  ev2StatusesMap: Ev2ExperimentStatusResults,
  rolloutIdConfigFilenameMap: Record<
    string,
    { fileName: string; environment: string }
  >,
) => {
  const ev2StatusMap: PipelineStatusMap = {};
  Object.keys(ev2StatusesMap).forEach((rolloutId) => {
    const configFileName = rolloutIdConfigFilenameMap[rolloutId]?.fileName;
    const environment = rolloutIdConfigFilenameMap[rolloutId]?.environment;
    const ev2Statuses = ev2StatusesMap[rolloutId].sort(
      (a, b) =>
        new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
    );

    if (configFileName) {
      if (!ev2StatusMap[configFileName]) {
        ev2StatusMap[configFileName] = {
          [RC_TYPE]: null,
          [MPAC_TYPE]: null,
          [PROD_TYPE]: null,
        };
      }
      // Calculate how long the rollout has been running based on the latest status start time
      const latestStatus = ev2Statuses[ev2Statuses.length - 1];
      const latestStatusStartDate = new Date(latestStatus.startTime);
      const now = new Date();
      const daysSinceStart = Math.floor(
        (now.getTime() - latestStatusStartDate.getTime()) /
          (1000 * 60 * 60 * 24),
      );

      const latestCurrentStatus = ev2StatusMap[configFileName][environment];
      if (latestCurrentStatus) {
        const latestCurrentStatusStartDate = new Date(
          latestCurrentStatus.statuses[latestCurrentStatus.statuses.length - 1]
            .startTime,
        );
        // Only update the status map if the new status has a more recent start time than the existing one
        if (latestCurrentStatusStartDate < latestStatusStartDate) {
          ev2StatusMap[configFileName][environment] = {
            statuses: ev2Statuses,
            daysSinceStart,
          };
        }
      } else {
        ev2StatusMap[configFileName][environment] = {
          statuses: ev2Statuses,
          daysSinceStart,
        };
      }
    }
  });

  return ev2StatusMap;
};
